import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import Quill from 'quill';

// Import style-based attributors
const AlignStyle = Quill.import('attributors/style/align');
const BackgroundStyle = Quill.import('attributors/style/background');
const ColorStyle = Quill.import('attributors/style/color');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');

// Register style attributors to use inline styles (overrides class-based)
Quill.register('formats/align', AlignStyle, true);
Quill.register('formats/background', BackgroundStyle, true);
Quill.register('formats/color', ColorStyle, true);
Quill.register('formats/font', FontStyle, true);
Quill.register('formats/size', SizeStyle, true);

@Component({
  selector: 'mh-editor-html',
  templateUrl: './editor-html.component.html',
  styleUrls: ['./editor-html.component.scss'],
  standalone: false,
})
export class EditorHtmlComponent implements OnInit, OnChanges, OnDestroy {
  quillConfig = {
    modules: {
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ header: 1 }, { header: 2 }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ font: [] }],
          ['link', 'image'],
          ['clean'],
        ],
      },
    },
    placeholder: '',
  };

  editorData = '';

  @Input() data: string;
  @Input() reset: any;
  @Input() key: string;
  @Output() ready: EventEmitter<any> = new EventEmitter();
  @Output() change: EventEmitter<any> = new EventEmitter();
  @Output() refresh: EventEmitter<any> = new EventEmitter();

  editorsList: any = [];

  private isInternalChange = false;

  ngOnInit() {
    if (this.data) {
      this.editorData = this.data;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reset) {
      const { currentValue } = changes.reset;
      if (currentValue && currentValue[0]) {
        this.editorsList.forEach((instance: any) => {
          if (instance.field === currentValue[0]) {
            instance.editor.root.innerHTML = this.data || '';
          }
        });
      }
    }
    if (changes.data && !this.isInternalChange) {
      const { currentValue, firstChange } = changes.data;
      this.editorData = currentValue || '';

      if (firstChange) {
        this.editorsList.forEach((instance: any) => {
          if (instance.field === this.key) {
            this.refresh.emit(true);
            instance.editor.root.innerHTML = currentValue || '';
          }
        });
      }
    }
    this.isInternalChange = false;
  }

  ngOnDestroy() {
    this.editorsList = null;
  }

  setEditor(event: any) {
    this.editorsList.push({
      editor: event,
      field: this.key,
    });
    this.ready.emit(event);
  }

  setValue(event: any) {
    this.isInternalChange = true;
    const html = event.html || '';

    // Apply image fix (converts image URLs to <img> tags)
    const htmlForEditor = this.fixImgTag(html);

    // Update the editor if an image conversion was detected
    if (this.detectImageConversion(html, htmlForEditor)) {
      const editorInstance = this.editorsList.find((instance: any) => instance.field === this.key);
      if (editorInstance) {
        editorInstance.editor.root.innerHTML = htmlForEditor;
      }
    }

    // Normalize paragraphs and emit HTML
    const htmlToEmit = this.normalizeSpaces(htmlForEditor);
    this.change.emit(htmlToEmit);
    this.refresh.emit(false);
  }

  // Converts Quill HTML keeping each <p> with its styles
  // Empty paragraphs are converted to <br> for blank lines
  normalizeSpaces(html: string): string {
    if (!html) return '';

    // Wrap naked text nodes in <span> for email client compatibility
    let result = this.wrapNakedTextNodes(html);

    // Convert empty paragraphs to <br> (blank lines only)
    result = result.replace(/<p[^>]*>(<br>|\s*)<\/p>/gi, '<br>');

    // Clean up excessive <br> tags (max 2)
    result = result.replace(/(<br>){3,}/gi, '<br><br>');

    // Remove leading <br> only, allow trailing <br> (max 2)
    result = result.replace(/^(<br>)+/, '');
    result = result.replace(/(<br>){3,}$/, '<br><br>');

    // Normalize &nbsp; to regular spaces AFTER all DOM manipulations
    result = result.replace(/&nbsp;/g, ' ');

    return result;
  }

  // Wraps text nodes that are direct children of block elements in <span>
  // formatted elements (like <strong>) displays incorrectly
  wrapNakedTextNodes(html: string): string {
    if (!html) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');
    const body = doc.body;

    // Process all block-level elements that may contain mixed content
    const blockElements = body.querySelectorAll('p, div, li, td, th, h1, h2, h3, h4, h5, h6');

    blockElements.forEach((block) => {
      const childNodes = Array.from(block.childNodes);

      childNodes.forEach((node) => {
        // Check if it's a text node with actual content (not just whitespace)
        if (node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim()) {
          const span = doc.createElement('span');
          // Add explicit font-weight: normal to prevent email clients (especially Outlook)
          // from inheriting bold style from sibling <strong> elements
          span.style.fontWeight = 'normal';
          span.textContent = node.textContent;
          block.replaceChild(span, node);
        }
      });
    });

    return body.innerHTML;
  }

  // Checks if an <img> tag was added that didn't exist before
  detectImageConversion(originalHtml: string, convertedHtml: string): boolean {
    const originalCount = (originalHtml.match(/<img /g) || []).length;
    const convertedCount = (convertedHtml.match(/<img /g) || []).length;
    return convertedCount > originalCount;
  }

  // Converts image URLs to <img> tags and normalizes width
  fixImgTag(html: string): string {
    if (!html) return '';

    // Convert image URLs inside <p> or <a> to <img> tags
    // Pattern: <p> or <a> with optional attributes, containing only an image URL
    const urlPattern = /<(?:p|a)[^>]*>(https?:\/\/[^<]+\.(gif|jpg|jpeg|png|webp|svg)(\?[^<]*)?)<\/(?:p|a)>/gi;
    const withImages = html.replace(urlPattern, (_, url) => `<img style="max-width:485px;" src="${url}" />`);

    // Normalize all <img> with max-width
    return withImages.replace(/<img /g, '<img style="max-width:485px;"');
  }
}
